// Text and number rules of the Booking ranking page. Pure functions, no React: the words and the
// rounding of the canvas generator (Room_Repos/scripts/booking_ranking/concept/build_canvas_v4.py).
import { RANK_TIERS, RankTier, RankValue, rankTierOf } from '@ui/rank/rankScale';
import {
	BookingPositionCell,
	BookingPositionHotel,
	BookingPositionHotelMonth,
	BookingPositionMonth,
	BookingPositionScan,
	BookingPositionState,
} from './model/BookingPositionState';

export type { RankValue };

export const NOT_IN_500 = 'Not in top 500';
// A check reads the first 525 results of the City list (25 per page, without ads and repeats).
export const HEAD_SLOTS = 525;

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September',
	'October', 'November', 'December'];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Round half up, as the canvas (35.5 -> 36).
export const whole = (value: number): number => Math.floor(value + 0.5);

export const ordinal = (n: number): string => {
	const tens = n % 100;
	if (tens >= 10 && tens <= 20) return `${n}th`;
	return `${n}${({ 1: 'st', 2: 'nd', 3: 'rd' } as Record<number, string>)[n % 10] ?? 'th'}`;
};

export const formatCount = (n: number): string => n.toLocaleString('en-GB');

export const percentText = (share: number | null): string => (share === null ? '–' : `${whole(share)}%`);

export const euroText = (price: number | null): string => (price === null ? '–' : `€${whole(price)}`);

export const scoreText = (score: number | null): string => (score === null ? '–' : score.toFixed(1));

export const rankValue = (value: number | null, after500: boolean): RankValue =>
	after500 ? 'over' : (value === null ? null : whole(value));

export const positionText = (value: number | null, after500: boolean): string =>
	after500 ? NOT_IN_500 : (value === null ? '–' : String(whole(value)));

export const hasTypical = (hotel: BookingPositionHotel): boolean => hotel.typical !== null || hotel.typicalAfter500;

// "25–42", "382 or worse" (the range ends outside the top 500), "Not in top 500", or "–".
export const middleHalfText = (hotel: BookingPositionHotel): string => {
	if (!hasTypical(hotel)) return '–';
	if (hotel.middleHalfLoAfter500) return NOT_IN_500;
	if (hotel.middleHalfHiAfter500) return `${hotel.middleHalfLo} or worse`;
	return `${hotel.middleHalfLo}–${hotel.middleHalfHi}`;
};

// "3 ≈" in the # column: the place, and the mark when the hotel is level with a neighbour.
export const compsetPlaceText = (hotel: BookingPositionHotel): string =>
	hotel.compsetRank === null ? '–' : `${hotel.compsetRank}${hotel.levelWithNeighbour ? ' ≈' : ''}`;

const parse = (iso: string) => {
	const [y, m, d] = iso.split('-').map(Number);
	return { y, m, d, weekday: new Date(Date.UTC(y, m - 1, d)).getUTCDay() };
};

// "Tue 6 Oct"
export const nightLabel = (iso: string): string => {
	const { m, d, weekday } = parse(iso);
	return `${WEEKDAYS[weekday]} ${d} ${MONTHS_SHORT[m - 1]}`;
};

// "Tue 6 Oct 2026"
export const nightLabelLong = (iso: string): string => `${nightLabel(iso)} ${parse(iso).y}`;

// "4 Oct 2026"
export const dayLabel = (iso: string): string => {
	const { y, m, d } = parse(iso);
	return `${d} ${MONTHS_SHORT[m - 1]} ${y}`;
};

// "3 Jan"
export const dayMonthLabel = (iso: string): string => {
	const { m, d } = parse(iso);
	return `${d} ${MONTHS_SHORT[m - 1]}`;
};

// month = YYYY-MM. "Oct 2026", "Oct", "October", "October 2026".
export const monthLabel = (month: string, form: 'short' | 'shortYear' | 'long' | 'longYear' = 'shortYear'): string => {
	const [y, m] = month.split('-').map(Number);
	if (form === 'short') return MONTHS_SHORT[m - 1];
	if (form === 'long') return MONTHS_LONG[m - 1];
	if (form === 'longYear') return `${MONTHS_LONG[m - 1]} ${y}`;
	return `${MONTHS_SHORT[m - 1]} ${y}`;
};

export const nightsWord = (n: number): string => `${n} night${n === 1 ? '' : 's'}`;

// A month with fewer than 4 scanned nights (the tail month) is not a "full" month.
const FULL_MONTH_NIGHTS = 4;
// The first scan checked every night to 3 Jan, then every 3rd night: a month with fewer than 20 nights.
const SPARSE_MONTH_NIGHTS = 20;

export const fullMonths = (months: BookingPositionMonth[]): BookingPositionMonth[] =>
	months.filter((m) => m.nights >= FULL_MONTH_NIGHTS);

// "Jan–Mar": the months that the first scan checked every 3rd night (the short tail month left out).
export const sparseRangeText = (months: BookingPositionMonth[]): string | null => {
	const sparse = months.filter((m) => m.nights < SPARSE_MONTH_NIGHTS);
	const shown = sparse.length > 1 && sparse[sparse.length - 1].nights < FULL_MONTH_NIGHTS ? sparse.slice(0, -1) : sparse;
	if (shown.length === 0) return null;
	const first = monthLabel(shown[0].month, 'short');
	const last = monthLabel(shown[shown.length - 1].month, 'short');
	return first === last ? first : `${first}–${last}`;
};

// The window words of the page: "first scan of 4 Oct 2026" or "the last 7 days" (D11).
export const windowText = (scan: BookingPositionScan): string =>
	scan.window === 'LAST_7_DAYS' ? 'the last 7 days' : `first scan of ${dayLabel(scan.scanDate)}`;

// The window words of the chart: the first scan names its every-3rd-night months.
export const chartWindowText = (scan: BookingPositionScan, months: BookingPositionMonth[]): string => {
	if (scan.window === 'LAST_7_DAYS') return 'the last 7 days';
	const sparse = sparseRangeText(months);
	return sparse === null ? windowText(scan) : `${windowText(scan)}, ${sparse} every 3rd night`;
};

export const sameCityHotels = (state: BookingPositionState): BookingPositionHotel[] => state.hotels.filter((h) => h.sameCity);

export const otherCityHotels = (state: BookingPositionState): BookingPositionHotel[] => state.hotels.filter((h) => !h.sameCity);

export const mainHotel = (state: BookingPositionState): BookingPositionHotel | undefined => state.hotels.find((h) => h.isMain);

// The insight line: the first and the last full month of the own hotel, the city price share against
// the typical position. A month without both values gives no line.
export const insightText = (state: BookingPositionState): string | null => {
	const main = mainHotel(state);
	const full = fullMonths(state.months);
	if (main === undefined || full.length < 2) return null;
	const find = (month: string): BookingPositionHotelMonth | undefined => main.months.find((m) => m.month === month);
	const a = find(full[0].month);
	const b = find(full[full.length - 1].month);
	if (a?.typical == null || a.cityDearerShare === null || b?.typical == null || b.cityDearerShare === null) return null;
	const city = state.bookingCity.name;
	return `On nights when you are dear against all of ${city}, Booking.com tends to show you lower: `
		+ `in ${monthLabel(a.month, 'short')} you were dearer than ${percentText(a.cityDearerShare)} of the city and typically at ${whole(a.typical)}; `
		+ `in ${monthLabel(b.month, 'short')}, dearer than ${percentText(b.cityDearerShare)} and at ${whole(b.typical)}. `
		+ 'The season and the time before the stay also change a month, so this is a link, not a proven rule.';
};

// The nights where RoomPulse has no Booking.com price check and a hotel is not found: a gap (no data).
export const noPriceCheckNights = (state: BookingPositionState): string[] => {
	const dates = new Set<string>();
	for (const hotel of sameCityHotels(state)) {
		for (const cell of hotel.cells) {
			if (cell.state === 'NOT_FOUND' && !cell.hasPriceCheck) dates.add(cell.date);
		}
	}
	return [...dates].sort();
};

export type PlaceChange = { kind: 'steady' | 'up' | 'down', places: number };

// Change vs 4 weeks: the Typical position of the last 7 days against the same 7 days 4 weeks earlier.
// Steady = a move of not more than half the middle half (at least 2 places). Up = a better place.
export const placeChange = (hotel: BookingPositionHotel): PlaceChange | null => {
	if (hotel.typical4WeeksEarlier === null || hotel.typical === null || hotel.middleHalfLo === null) return null;
	const now = whole(hotel.typical);
	const moved = hotel.typical4WeeksEarlier - now;
	const half = ((hotel.middleHalfHi ?? now) - hotel.middleHalfLo) / 2;
	if (Math.abs(moved) <= Math.max(half, 2)) return { kind: 'steady', places: 0 };
	return { kind: moved > 0 ? 'up' : 'down', places: Math.abs(moved) };
};

// +0.1 / −0.1 against 4 weeks earlier; null when no older score exists or the score did not change.
export const reviewChange = (hotel: BookingPositionHotel): number | null => {
	if (hotel.reviewScore === null || hotel.reviewScore4WeeksEarlier === null) return null;
	const change = Math.round((hotel.reviewScore - hotel.reviewScore4WeeksEarlier) * 10) / 10;
	return change === 0 ? null : change;
};

export const hasChangeHistory = (state: BookingPositionState): boolean =>
	state.hotels.some((h) => h.typical4WeeksEarlier !== null);

// The value of one night on the chart: a place, 'over' (the "500+" band), or null (a gap).
export const cellRankValue = (cell: BookingPositionCell | undefined): RankValue => {
	if (cell === undefined) return null;
	if (cell.state === 'POSITION' && cell.position !== null) return whole(cell.position);
	if (cell.state === 'NOT_IN_500') return 'over';
	return null;
};

// The tooltip word of a gap on the chart (gapLabels of RankLineChart, design-system v1.5.1): "sold out",
// "not found", or "no data" when RoomPulse has no Booking.com price check of the night. null = not a gap.
export const cellGapLabel = (cell: BookingPositionCell | undefined): string | null => {
	if (cell === undefined || cellRankValue(cell) !== null) return null;
	if (cell.state === 'SOLD_OUT') return 'sold out';
	if (cell.state === 'MIN_STAY') return 'minimum stay';
	if (cell.state === 'NOT_FOUND') return cell.hasPriceCheck ? 'not found' : 'no data';
	return null;
};

// The tier of a place, for the night grid and the month table of the Detailed view (feature-local
// Screen Parts, user decision of 2026-10-05). The bands and the classes come from the design system
// (rankScale of v1.5.0), so the boxes use the colours of RankTierChip.
export const tierLabel = (tier: RankTier): string => (tier.limit === 'over' ? NOT_IN_500 : `Top ${tier.limit}`);

export const POSITION_TIERS: readonly RankTier[] = RANK_TIERS;

export const positionTier = (value: RankValue): RankTier | undefined => rankTierOf(value);

const checksWord = (n: number): string => `${n} check${n === 1 ? '' : 's'}`;

export type GridCellText = { main: string, sub: string, price: string | null, title: string };

// The texts of one box of the night grid: the middle position, the best and the worst check (or how
// many checks found the hotel), and under the line the city share and the compset price place.
export const gridCellText = (cell: BookingPositionCell): GridCellText => {
	const hasPrice = cell.cityDearerShare !== null && cell.compsetPriceRank !== null;
	const price = hasPrice ? `${percentText(cell.cityDearerShare)} · ${ordinal(cell.compsetPriceRank as number)}` : 'price not seen';
	const priceTitle = hasPrice
		? ` Price: dearer than ${percentText(cell.cityDearerShare)} of the city, ${ordinal(cell.compsetPriceRank as number)} cheapest in the compset.`
		: ' No check found the hotel, so the scan has no price for this night.';
	if (cell.state === 'POSITION' && cell.position !== null) {
		const sub = cell.checksFound === cell.checks
			? (cell.best !== cell.worst ? `${cell.best}–${cell.worst}` : `all ${cell.checks} the same`)
			: `${cell.checksFound} of ${cell.checks} found`;
		const place = cell.compsetRank !== null && cell.compsetOf !== null
			? ` ${ordinal(cell.compsetRank)} of ${cell.compsetOf} in the compset this night.` : '';
		return {
			main: String(whole(cell.position)), sub, price,
			title: `Middle of the checks: ${whole(cell.position)}. Best ${cell.best}, worst ${cell.worst} (checks that found it).${place}${priceTitle}`,
		};
	}
	if (cell.state === 'NOT_IN_500') {
		const sub = cell.checksFound === 1 && cell.best !== null ? `found once: ${cell.best}` : `${cell.checksFound} of ${cell.checks} found`;
		return {
			main: 'Not in 500', sub, price,
			title: `${NOT_IN_500}: the middle of the checks is not in the top 500. `
				+ `${checksWord(cell.checks - cell.checksFound)} did not find it in the first ${HEAD_SLOTS} results.${priceTitle}`,
		};
	}
	if (cell.state === 'SOLD_OUT') {
		return {
			main: 'Sold out', sub: '', price: null,
			title: 'Sold out on Booking.com this night (RoomPulse\'s own Booking.com price checks: every room sold out).',
		};
	}
	if (cell.state === 'MIN_STAY') {
		return {
			main: 'Min. stay', sub: '', price: null,
			title: 'Minimum stay: another channel asks for a longer stay than the checks this night, so this night is left out.',
		};
	}
	if (!cell.hasPriceCheck) {
		return {
			main: 'Not found', sub: 'no data', price: null,
			title: `Not found in any of the ${cell.checks} checks, and RoomPulse has no Booking.com price check of this night, so this night is left out.`,
		};
	}
	return {
		main: 'Not found', sub: 'sold out?', price: null,
		title: `Not found in any of the ${cell.checks} checks. RoomPulse's latest price check before the scan shows it sold out, `
			+ 'but not every check of the last 3 days, so this night is left out.',
	};
};

// "82 with a position" + "37 sold out · 1 not found" for the Compset details table.
export const nightCountTexts = (hotel: BookingPositionHotel): { first: string, rest: string } => {
	const c = hotel.nightCounts;
	const rest = [
		c.notIn500 > 0 ? `${c.notIn500} not in top 500` : null,
		c.soldOut > 0 ? `${c.soldOut} sold out` : null,
		c.notFound > 0 ? `${c.notFound} not found` : null,
	].filter((t): t is string => t !== null);
	return { first: `${c.withPosition} with a position`, rest: rest.join(' · ') };
};

// Keep the ?mockState switch (and any other search) when the page links to its other view.
export const withSearch = (path: string, search: string): string => `${path}${search}`;
